import { useCallback, useEffect, useState } from 'react'
import type { WorkerDetail, WorkerRecord } from '@shared/worker'
import type { GroupDetail, GroupRecord } from '@shared/groups'
import { workersApi } from '../lib/api'

/** One worker, kept live: refetched whenever the server reports a change to it. */
export function useWorker(id: string | null): { worker: WorkerDetail | null; error: string | null; reload: () => Promise<void> } {
  const [worker, setWorker] = useState<WorkerDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const reload = useCallback(async () => {
    if (!id) return
    try {
      setWorker(await workersApi.get(id))
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the worker')
    }
  }, [id])
  useEffect(() => {
    if (!id) return
    void reload()
    let t: ReturnType<typeof setTimeout> | null = null
    const off = workersApi.subscribe((changed) => {
      if (changed !== id) return
      if (t) clearTimeout(t)
      t = setTimeout(() => void reload(), 150) // coalesce bursts (a fill moves several things at once)
    })
    return () => {
      off()
      if (t) clearTimeout(t)
    }
  }, [id, reload])
  return { worker, error, reload }
}

/** All of the user's workers, kept live. */
export function useWorkers(): { workers: WorkerRecord[] | null; error: string | null; reload: () => Promise<void> } {
  const [workers, setWorkers] = useState<WorkerRecord[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const reload = useCallback(async () => {
    try {
      setWorkers(await workersApi.list())
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load workers')
    }
  }, [])
  useEffect(() => {
    void reload()
    let t: ReturnType<typeof setTimeout> | null = null
    const off = workersApi.subscribe(() => {
      if (t) clearTimeout(t)
      t = setTimeout(() => void reload(), 300)
    })
    return () => {
      off()
      if (t) clearTimeout(t)
    }
  }, [reload])
  return { workers, error, reload }
}

/** One worker group with its members, kept live (the group or any member changing refetches it). */
export function useWorkerGroup(id: string | null): { group: GroupDetail | null; error: string | null; reload: () => Promise<void> } {
  const [group, setGroup] = useState<GroupDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const reload = useCallback(async () => {
    if (!id) return
    try {
      setGroup(await workersApi.group(id))
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the group')
    }
  }, [id])
  useEffect(() => {
    if (!id) return
    void reload()
    let t: ReturnType<typeof setTimeout> | null = null
    const off = workersApi.subscribe((changed) => {
      if (changed !== id && !group?.spec.members.some((m) => m.workerId === changed)) return
      if (t) clearTimeout(t)
      t = setTimeout(() => void reload(), 200)
    })
    return () => {
      off()
      if (t) clearTimeout(t)
    }
  }, [id, reload, group?.spec.members])
  return { group, error, reload }
}

/** All of the user's worker groups, kept live. */
export function useWorkerGroups(): { groups: GroupRecord[] | null; reload: () => Promise<void> } {
  const [groups, setGroups] = useState<GroupRecord[] | null>(null)
  const reload = useCallback(async () => {
    try {
      setGroups(await workersApi.groups())
    } catch {
      /* the list shows what it has */
    }
  }, [])
  useEffect(() => {
    void reload()
    let t: ReturnType<typeof setTimeout> | null = null
    const off = workersApi.subscribe(() => {
      if (t) clearTimeout(t)
      t = setTimeout(() => void reload(), 300)
    })
    return () => {
      off()
      if (t) clearTimeout(t)
    }
  }, [reload])
  return { groups, reload }
}
